import React, { createContext, useContext } from 'react'
import { FormControl, MenuItem, Select } from '@mui/material'

/**
 * The dashboard's job set, for the editor's pickers (#1229 C4, Matt 3 Sep).
 *
 * On a multi-job dashboard every table picker — the chart/table widget's
 * tree and the Gen AI wizard's source browser — lets the user pick the JOB
 * first, then the table. The editor provides the set once here; a picker
 * shows the selector only when there is more than one job, so single-job
 * dashboards look exactly as before. Display names, never job names.
 */
export interface DashboardJobOption {
  name: string
  displayName?: string
}

export interface DashboardJobsValue {
  jobs: DashboardJobOption[]
  primaryJob: string
}

const DashboardJobsContext = createContext<DashboardJobsValue | null>(null)

export const DashboardJobsProvider = DashboardJobsContext.Provider

/** The job set, or null when no editor provides one (single-job surfaces, tests). */
export function useDashboardJobs(): DashboardJobsValue | null {
  return useContext(DashboardJobsContext)
}

export function jobDisplayName(jobs: DashboardJobOption[] | undefined, job: string): string {
  return jobs?.find((j) => j.name === job)?.displayName || job
}

/**
 * The "pick the job" control a picker renders above its tree. Renders
 * nothing unless the dashboard has more than one job.
 */
export const DashboardJobSelect: React.FC<{
  value: string
  onChange: (job: string) => void
  ariaLabel?: string
}> = ({ value, onChange, ariaLabel = 'Table job' }) => {
  const dashboardJobs = useDashboardJobs()
  if (!dashboardJobs || dashboardJobs.jobs.length < 2) return null
  return (
    <FormControl size="small" fullWidth>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as string)}
        inputProps={{ 'aria-label': ariaLabel }}
        sx={{ fontSize: '0.8125rem' }}
      >
        {dashboardJobs.jobs.map((j) => (
          <MenuItem key={j.name} value={j.name}>{j.displayName || j.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
